import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { Suspense, lazy, useEffect, useRef } from 'react';
import { ProtectedRoute, RoleRoute, setUnauthorizedHandler, useAuthStore } from '@/auth';
import { useResolvedRole, roleHome } from '@/hooks/useResolvedRole';
import { servesPortal } from '@/lib/portalHost';
import { PageLayout } from '@/components/PageLayout';
import { FullPageLoading } from '@/components/LoadingState';
import { Toaster } from '@/components/ui/toaster';
import Login from '@/pages/auth/Login';
import Login2FA from '@/pages/auth/Login2FA';
import Signup from '@/pages/auth/Signup';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import PatientList from '@/pages/patients/PatientList';
import PatientShell from '@/pages/patients/PatientShell';
import GlobalResources from '@/pages/resources/GlobalResources';
import Clinicians from '@/pages/clinicians/Clinicians';
import CgmReport from '@/pages/cgm/CgmReport';
import Profile from '@/pages/account/Profile';
import NotFound from '@/pages/NotFound';
import Forbidden from '@/pages/Forbidden';
import { clearOpenPanelClinician } from '@/lib/openpanel';

// The patient portal is lazy-loaded so its bundle stays out of the clinician's
// initial load (and vice versa) — the cheap half of the eventual separate-build
// hardening (see the project plan).
const PatientPortal = lazy(() => import('@/pages/me/PatientPortal'));

const Logout = () => {
  const navigate = useNavigate();
  useEffect(() => {
    import('@/auth').then(({ useAuthStore }) => {
      useAuthStore.getState().logout().finally(() => navigate('/login', { replace: true }));
    });
  }, [navigate]);
  return null;
};

// Role-aware landing. `/` sends a patient to their portal and a clinician to
// the patient roster; an unauthenticated visitor to /login; a user with no
// portal role to /forbidden (via roleHome). Role resolution is server-derived
// (never token-shape) — see useResolvedRole.
const RootRedirect = () => {
  const token = useAuthStore((s) => s.token);
  const { role, isLoading } = useResolvedRole();
  if (!token) return <Navigate to="/login" replace />;
  if (isLoading) return <FullPageLoading />;
  return <Navigate to={roleHome(role)} replace />;
};

export default function App() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const clearedAnonymousRef = useRef(false);

  useEffect(() => {
    if (!token && !clearedAnonymousRef.current) {
      clearOpenPanelClinician();
      clearedAnonymousRef.current = true;
    } else if (token) {
      clearedAnonymousRef.current = false;
    }
  }, [token]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (window.location.pathname !== '/login') {
        navigate('/login', { replace: true });
      }
    });
    // No app-level forbidden handler. A 403 from a sub-resource (e.g. report
    // metrics on the patient summary) used to navigate the whole app to
    // /forbidden, knocking the user off the page. Pages that want a 403 on
    // their *primary* fetch to navigate can install one locally.
  }, [navigate]);

  return (
    <>
      <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/login" element={<Login />} />
        <Route path="/login/2fa" element={<Login2FA />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />

        <Route element={<ProtectedRoute />}>
          {/* /forbidden is reachable by any authenticated user, regardless of role. */}
          <Route path="/forbidden" element={<Forbidden />} />

          {/* Clinician area — role-gated, absent on patient hosts. */}
          {servesPortal('clinician') && (
            <Route element={<RoleRoute allow="clinician" />}>
              <Route
                path="/patients"
                element={<PageLayout><PatientList /></PageLayout>}
              />
              <Route
                path="/patients/:id/*"
                element={<PageLayout><PatientShell /></PageLayout>}
              />
              <Route
                path="/resources"
                element={<PageLayout><GlobalResources /></PageLayout>}
              />
              <Route
                path="/clinicians"
                element={<PageLayout><Clinicians /></PageLayout>}
              />
              <Route
                path="/account"
                element={<PageLayout><Profile /></PageLayout>}
              />
              <Route path="/cgm-report/:patientId" element={<CgmReport />} />
            </Route>
          )}

          {/* Patient area — role-gated, lazy-loaded, self-owned layout, patient hosts only. */}
          {servesPortal('patient') && (
            <Route element={<RoleRoute allow="patient" />}>
              <Route
                path="/me/*"
                element={
                  <Suspense fallback={<FullPageLoading />}>
                    <PatientPortal />
                  </Suspense>
                }
              />
            </Route>
          )}
        </Route>

        {/* Patient hosts do not register clinician routes. `/patients` and any
            other unknown path go through the same landing as `/` (`/me` when
            signed in, `/login` when not) instead of the clinician 404. */}
        <Route
          path="*"
          element={servesPortal('clinician') ? <NotFound /> : <RootRedirect />}
        />
      </Routes>
      <Toaster />
    </>
  );
}
